import { assertNever, LanguageCode, Survey, SurveyData, SurveyQuestion, SurveyQuestionType, SurveyScore, SurveyStatus, SurveyTheme } from "..";


export const surveyHelper = {
    getQuestionValue,
    getThemeValue,
    getValidLanguageCodes,
    getAllSurveyQuestions,
    getSurveyQuestionType,
    getSurveyQuestionSelectionValues,
    isValidSelectionValue,
    getSelectionScore,
    getAllThemeQuestions,
    getThemeQuestionIndices,
    getThemeQuestionRanges,
    getSurveyQuestionIndex,
    getQuestionIndex,
    getAllIndicators,
    getAllIndicatorsWithCount,
    getSurveyStatus,
    findLastUnconditionalQuestion,
    questionHasConditionalTriggerQuestion
};

const DEFAULT_SELECTION_VALUES = ['1', '2', '3', '4', '5'] as const;


function getQuestionValue(survey:Survey, themeIndex:number, questionIndex:number, languageCode:LanguageCode)
{
    if(languageCode === survey.languageCode)
        return survey.themes[themeIndex].questions[questionIndex].title;
    else
        return survey.themes[themeIndex].questions[questionIndex].translations.find(t => t.languageCode === languageCode)?.text || 'ERROR';
}

function getThemeValue(survey:Survey, themeIndex:number, languageCode:LanguageCode)
{
    if(languageCode === survey.languageCode)
        return survey.themes[themeIndex].name;
    else
        return survey.themes[themeIndex].translations.find(t => t.languageCode === languageCode)?.text || 'ERROR';
}

function getValidLanguageCodes(survey:SurveyData):LanguageCode[]
{
    return [
        survey.languageCode,
        ...survey.translationLanguages
    ].sort();
}

function getAllSurveyQuestions(survey: SurveyData): { themeIndex:number, question: SurveyQuestion }[] {
    return getAllThemeQuestions(survey.themes);
}

export function getSurveyQuestionType(question:SurveyQuestion):SurveyQuestionType {
    return question.type || 'default';
}

export function getSurveyQuestionSelectionValues(question:SurveyQuestion):string[] {
    if(question.type === 'default')
    {
        return [...DEFAULT_SELECTION_VALUES];
    }

    else if(question.type === 'custom')
    {
        return question.options.map(o => o.value);
    }

    else if(question.type === 'multiple-choice')
    {
        return question.options.map(o => o.value);
    }

    else
    {
        assertNever(question);
        throw new Error('Unsupported question type');
    }
}

export function isValidSelectionValue(question:SurveyQuestion, selectionValue:string):boolean {
    return getSurveyQuestionSelectionValues(question).includes(selectionValue);
}

export function getSelectionScore(question:SurveyQuestion, selectionValue:string):SurveyScore | undefined
{
    if(!isValidSelectionValue(question, selectionValue))
        throw new Error(`Invalid selection value ${selectionValue} for question`);

    if(question.type === 'default')
        return Number.parseInt(selectionValue, 10) as SurveyScore;

    else if(question.type === 'custom')
    {
        if(question.scored === true)
        {
            const option = question.options.find(o => o.value === selectionValue);
            if(!option)
            {
                throw new Error(`Invalid selection value ${selectionValue} for question`);
            }
            return option.score;
        }
        else
            return undefined;
    }

    else if(question.type === 'multiple-choice')
        return undefined;

    else {
        assertNever(question);
        throw new Error('Unsupported question type');
    }
}

export function getAllThemeQuestions(themes:SurveyTheme[]): { themeIndex:number, question: SurveyQuestion }[] {
    return themes.flatMap((t,i) =>
        t.questions.map(q => {
            return { themeIndex:i, question: q }
        })
    );
}

export function getThemeQuestionIndices(survey: SurveyData, theme: string) {
    const themeIndex = survey.themes.findIndex(t => t.name === theme);
    if (themeIndex < 0)
        throw new Error(`Theme ${theme} does not exist`);

    let start = 0;
    for (let i = 0; i < themeIndex; i++)
        start += survey.themes[i].questions.length;

    return { start, length: survey.themes[themeIndex].questions.length };
}

// Including endIndexes means empty themes will have to be ignored (they don't fit in)
// (2) 0-1
// (0) 1-1 ? Wrong. It doesn't occupy any indexes
// (1) 2-2
// (2) 3-4
export function getThemeQuestionRanges(survey: SurveyData) {
    const ranges: { theme: string, startIndex: number, length: number }[] = [];

    let startIndex = 0;
    for (let themeIndex = 0; themeIndex < survey.themes.length; themeIndex++) {
        const length = survey.themes[themeIndex].questions.length
        ranges.push({
            theme: survey.themes[themeIndex].name,
            startIndex,
            length
        });
        startIndex = startIndex + length;
    }
    return ranges;
}

// Returns theme/question index for a given survey question total index
export function getSurveyQuestionIndex(survey: SurveyData, answerSelectionIndex: number) {
    const ranges = getThemeQuestionRanges(survey);
    for (let themeIndex = 0; themeIndex < survey.themes.length; themeIndex++) {
        const range = ranges[themeIndex];
        if (answerSelectionIndex >= range.startIndex
            && answerSelectionIndex < range.startIndex + range.length)
            return {
                themeIndex,
                questionIndex: answerSelectionIndex - range.startIndex
            };
        /// Something here
    }
    return null;
}

export function getQuestionIndex(survey:SurveyData, themeIndex:number, questionIndex:number)
{
    let i = 0;
    for(let ti=0; ti<survey.themes.length; ti++){
        for(let qi=0; qi<survey.themes[ti].questions.length; qi++){
            if(ti === themeIndex && qi === questionIndex)
                return i;
            i++;
        }
    }
    return -1;
}

export function getAllIndicators(survey: SurveyData) {
    return getAllIndicatorsWithCount(survey).map(i => i.indicator);
}

export function getAllIndicatorsWithCount(survey: SurveyData)
    :{indicator:string, count:number}[] 
{
    const allIndicators = survey.themes.flatMap(t => t.questions.flatMap(q => q.indicators));

    const counts:{indicator:string,count:number}[] = [];
    for(const i of allIndicators)
    {
        let counter = counts.find(c => c.indicator === i);
        if(!counter){
            counter = {indicator:i, count:0};
            counts.push(counter);
        }
        counter.count = counter.count+1;
    }
    counts.sort((lhs,rhs)=>lhs.indicator.localeCompare(rhs.indicator));

    return counts;
}

export function getSurveyStatus(survey: Survey): SurveyStatus {
    let access: 'waiting' | 'open' | 'closed';
    const now = new Date();
    access = 'waiting';
    if (now > survey.start) {
        access = 'open';
        if (now > survey.end) {
            access = 'closed';
        }
    }
    const allSurveyQuestions = getAllSurveyQuestions(survey);
    const hasQuestions = allSurveyQuestions.length > 0;
    const hasEmptyThemes = survey.themes.findIndex(t => t.questions.length === 0) >= 0;

    return {
        access,
        canEdit: survey.answerCount === 0,
        hasQuestions,
        canSubmitAnswer: access === 'open' && hasQuestions,
        warning: hasEmptyThemes ? 'empty theme(s)' : ''
    };
}

function findLastUnconditionalQuestion(questions: SurveyQuestion[], startIndex?: number): SurveyQuestion | undefined {
    const start = startIndex !== undefined ? startIndex : questions.length - 1;
    for (let i = start; i >= 0; i--) {
        if (!questions[i].conditionalDisplay)
            return questions[i];
    }
    return undefined;
}

function questionHasConditionalTriggerQuestion(questions:SurveyQuestion[], questionIndex:number): boolean 
{
    const lastUnconditionalQuestion = findLastUnconditionalQuestion(questions, questionIndex - 1);
    if (!lastUnconditionalQuestion)
        return false;
    return true;
}